import { AfterViewInit, Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { CommonColor, DEFAULT_MARQUEE_SPEED_RATE, SPEED_RUN_TEXT_MARQUEE, USER_DASHBOARD } from '@core/constants';
import { TextMarqueeConfig, TextMarqueeModel } from '@core/models/models';
import { EXCREATIONSCREEN } from '@core/constants';
import { MarqueeSpeedService } from '@core/services/common';
import { Subscription } from 'rxjs';

@Component({
  selector: 'caremoni-text-marquee',
  templateUrl: './text-marquee.component.html',
  styleUrl: './text-marquee.component.scss',
  encapsulation: ViewEncapsulation.Emulated
})
export class TextMarqueeComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() data: any = null;
  @Input() config: TextMarqueeConfig = new TextMarqueeConfig();
  @Input() screen: string = '';
  @ViewChild('textContainer') textContainer!: any;
  isOverflowing: boolean = false;
  cssStyle: any = {};
  message: string = '';
  CommonColor = CommonColor;


  cssChangeColor: any = 'setColor';
  private resizeObserver!: ResizeObserver;
  private speedRate: number = DEFAULT_MARQUEE_SPEED_RATE;
  private speedRateSubscription!: Subscription;

  constructor(
    private marqueeSpeedService: MarqueeSpeedService,
  ) {

  }

  ngOnInit(): void {
    if(!this.config) this.config = new TextMarqueeConfig();
    this.cssStyle = { 
      'width' : this.config.width, 
      'height': this.config.height,
      '--marquee-start': '0px',
      '--marquee-end': '0px',
      '--marquee-duration': `10s`

    };
    if(this.data?.screen == EXCREATIONSCREEN) this.cssChangeColor = 'unsetColor';
    this.buildMessage();
  }

  /**
   * Derive the displayed text from `data`. Runs on every `data` change, not just at
   * init: a host list reusing its rows (trackBy) keeps this instance alive when a row
   * is edited, so a message built only once would keep showing the pre-edit text.
   */
  private buildMessage(): void {
    this.message = '';

    if(!this.data) {
      this.data = new TextMarqueeModel();
      return;
    }

    if(!this.checkArayType(this.data)) {
      this.message = this.data.message || '';
      return;
    }

    let groupedNodeID = Object.values(
      this.data.reduce((acc : any, item: any) => {
        const key = item.noteid;
        if (!acc[key]) acc[key] = [];
        acc[key].push(item);
        return acc;
      }, {} as { [key: string]: any[] })
    )
    groupedNodeID = groupedNodeID.sort((a : any, b: any) => {
      const dateA = new Date(a[0].date || a[0].upddate).getTime();
      const dateB = new Date(b[0].date || b[0].upddate).getTime();
      return dateB - dateA;
    });
    if (this.data[0]?.isMapByType) {
      // nghiệp vụ dành riêng cho attention_list, nếu có isMapByType thì group mesage theo type của message
      groupedNodeID.forEach((node: any) =>{
        const result = [...new Set(node.map((d:any) => d.type_name))]
        .map(type => `${type}：${node.filter((d:any) => d.type_name === type).map((d:any) => d.message).join(",")}`)
        .join("｜");
        this.message += "｜" + result;
      });
      if(groupedNodeID.length > 0) this.message = this.message.slice(1);
    } else {
      groupedNodeID.forEach((node: any) =>{ this.message += node?.map((item: any) => item.message).join(' '); });
    }
  }

  checkArayType(input: any) {
    return Array.isArray(input) ? true : false;
  }
  

  ngAfterViewInit(): void {
    this.observeResize();
    this.observeSpeedRate();
    this.scheduleMeasure();
  }

  private observeSpeedRate(): void {
    this.applySpeedRate(this.marqueeSpeedService.getSpeedRate());
    this.speedRateSubscription = this.marqueeSpeedService.speedRate$.subscribe(rate => this.applySpeedRate(rate));
  }

  private applySpeedRate(rate: number): void {
    this.speedRate = rate;
    this.updateAnimationDuration();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] || changes['config']) {
      if (changes['data']) {
        this.buildMessage();
      }
      setTimeout(() => this.scheduleMeasure(), 0);

    }
  }

  private observeResize(): void {
    this.resizeObserver = new ResizeObserver(() => this.scheduleMeasure());
    this.resizeObserver.observe(this.textContainer.nativeElement);
  }

  // đo sau 1 tick vì checkOverflow bật *ngIf mới tạo ra .marquee span
  private scheduleMeasure(): void {
    this.checkOverflow();
    setTimeout(() => this.updateAnimationDuration(), 0);
  }

  // kiểm tra text có bị overflow hay không
  private checkOverflow(): void {
    if (!this.textContainer) return;
    const element = this.textContainer.nativeElement;
    if(!element) return;
    this.isOverflowing = element.scrollWidth > element.offsetWidth;
  }

  ngOnDestroy(): void {
    this.resizeObserver.disconnect();
    this.speedRateSubscription?.unsubscribe();
  }

  private updateAnimationDuration(): void {
    if (!this.textContainer || !this.isOverflowing) return;
  
    const wrapperElement = this.textContainer.nativeElement;
    const textElement = wrapperElement.querySelector('.marquee span');
    if (!textElement) return;
  
    const containerWidth = wrapperElement.offsetWidth;
    const textWidth = textElement.offsetWidth;
  
    const distanceToMove = textWidth + containerWidth;
    const duration = distanceToMove / (SPEED_RUN_TEXT_MARQUEE * this.speedRate);
    const marqueeEnd = `-${textWidth}px`;
    const marqueeDuration = `${duration}s`;

    // giá trị không đổi thì không đụng vào DOM, để animation chạy tiếp
    if (this.cssStyle['--marquee-end'] === marqueeEnd && this.cssStyle['--marquee-duration'] === marqueeDuration) return;

    this.cssStyle['--marquee-start'] = `0px`;
    this.cssStyle['--marquee-end'] = marqueeEnd;
    this.cssStyle['--marquee-duration'] = marqueeDuration;
    this.restartAnimation(wrapperElement);
  }

  // ngStyle chỉ ghi ở chu kỳ change detection sau, nên set thẳng vào DOM trước khi restart
  private restartAnimation(wrapperElement: HTMLElement): void {
    wrapperElement.style.setProperty('--marquee-start', this.cssStyle['--marquee-start']);
    wrapperElement.style.setProperty('--marquee-end', this.cssStyle['--marquee-end']);
    wrapperElement.style.setProperty('--marquee-duration', this.cssStyle['--marquee-duration']);

    const marqueeElement = wrapperElement.querySelector('.marquee') as HTMLElement | null;
    if (!marqueeElement) return;

    // đổi duration giữa chừng làm animation nhảy pha, cho chạy lại từ đầu
    marqueeElement.style.animation = 'none';
    void marqueeElement.offsetWidth;
    marqueeElement.style.animation = '';
  }

}